/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PropTypes from "prop-types";
import styled from "styled-components";

import { useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import PersonIcon from "@material-ui/icons/Person";
import DescriptionIcon from "@material-ui/icons/Description";
import {
  Button,
  Divider,
  Backdrop,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from "@material-ui/core";

import NotesPanel from "../components/common/NotesPanel";
import Loader from "../components/Loader";
import AlertsIcon from "../components/common/AlertsIcon";
import UpsSpotLegInfo from "../components/spot-leg-details/UpsSpotLegInfo";
import UpsSpotLegClaimInfo from "../components/spot-leg-details/UpsSpotLegClaimInfo";
import BaseButton from "../components/common/BaseButton";
import SpotLegUpdate from "../components/spot-leg-details/SpotLegUpdate";
import ActionsModal from "../components/common/ActionsModal";
import RejectClaimModal from "../components/spot-leg-details/RejectClaimModal";

import types from "../helpers/types";
import { fetchSpotNotes, markSpotNotesRead } from "../slices/spotNotesSlice";
import { clearSpotLeg, fetchSpotLegById } from "../slices/spotLegSlice";
import { fetchCarrier, resetCarrier } from "../slices/carrierSlice";
import { fetchUser } from "../slices/userSlice";

import {
  assignSpotLegWithoutClaim,
  processSpotLegClaim,
} from "../slices/updateSpotLegSlice";
import CarrierAutoComplete from "../components/common/CarrierAutoComplete";
import UnsavedChangesModal from "../components/common/UnsavedChangesModal";

const StyledHeader = styled(({ theme, ...props }) => <Grid {...props} />)`
  background-color: ${(props) => props.theme.palette.background.headerBlue};
  padding: 12px 16px;
`;

const StyledGridContainer = styled(Grid)`
  padding: 16px;
`;

const StyledPersonIcon = styled(({ theme, ...otherProps }) => (
  <PersonIcon {...otherProps} />
))`
  vertical-align: bottom;
  color: ${(props) => props.theme.palette.primary.yellow};
`;

const StyledDescriptionIcon = styled(({ theme, ...otherProps }) => (
  <DescriptionIcon {...otherProps} />
))`
  vertical-align: bottom;
  color: ${(props) => props.theme.palette.primary.green};
`;

function UpsSpotLegDetails({
  handleNotification,
  setUnsavedModalOpen,
  unsavedModalOpen,
  setUnsavedChanges,
  unsavedChanges,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const spotLegDetails = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLeg
  );

  const isMultiLegRoute = useSelector(
    (state) => state.spotLegDetails.spotLeg.multiLegRoute
  );

  const spotLegDetailsRequestStatus = useSelector(
    (state) => state.spotLegDetails.requestStatus
  );

  const spotLegClaim = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLegClaim
  );

  const isFetchingCarrier = useSelector(
    (state) => state.carrier.requestStatus === types.requestStatuses.inProgress
  );

  const isFetchingUser = useSelector(
    (state) => state.user.requestStatus === types.requestStatuses.inProgress
  );

  const isFetchingSpotLegDetails = useSelector(
    (state) =>
      state.spotLegDetails.requestStatus === types.requestStatuses.inProgress
  );

  const spotLegUpdateRequestStatus = useSelector(
    (state) => state.updateSpotLeg.updateSpotLegRequestStatus
  );

  const assignSpotLegWithoutClaimRequestStatus = useSelector(
    (state) => state.updateSpotLeg.assignSpotLegWithoutClaimRequestStatus
  );

  const assignSpotLegWithoutClaimRequestFailureMessage = useSelector(
    (state) =>
      state.updateSpotLeg.assignSpotLegWithoutClaimRequestFailureMessage
  );

  const processClaimRequestStatus = useSelector(
    (state) => state.updateSpotLeg.processClaimRequestStatus
  );

  const processClaimRequestFailureMessage = useSelector(
    (state) => state.updateSpotLeg.processClaimRequestFailureMessage
  );

  const {
    notes,
    getRequestStatus: getSpotNotesRequestStatus,
    putRequestStatus: putSpotNotesRequestStatus,
  } = useSelector((state) => state.spotNotes);

  const [unableToPerformActionModalOpen, setUnableToPerformActionModalOpen] =
    useState(false);
  const [rejectClaimModalOpen, setRejectClaimModalOpen] = useState(false);
  const [carrierId, setCarrierId] = useState(null);
  const [carrierAssigned, setCarrierAssigned] = useState(false);
  const [isAssigningCarrier, setIsAssigningCarrier] = useState(false);
  const [cannotAssignCarrierModalOpen, setCannotAssignCarrierModalOpen] =
    useState(false);
  const [
    cannotAssignCarrierFailureMessage,
    setCannotAssignCarrierFailureMessage,
  ] = useState("");

  const handleCloseButtonClick = () => {
    if (unsavedChanges) {
      setUnsavedModalOpen(true);
    } else {
      handleClearingSpotLeg();
    }
  };

  const handleModalAffirmative = () => {
    setUnsavedModalOpen(false);
  };

  const handleModalNegative = () => {
    setUnsavedModalOpen(false);
    setUnsavedChanges(false);
    handleClearingSpotLeg();
  };

  const handleClearingSpotLeg = () => {
    if (spotLegClaim) {
      dispatch(resetCarrier());
    }
    dispatch(clearSpotLeg());
  };

  const handleRejectClaimButtonClick = () => {
    if (spotLegDetails.upsRouteId) {
      setRejectClaimModalOpen(true);
    } else {
      setUnableToPerformActionModalOpen(true);
    }
  };

  const handleAssignCarrierButtonClick = () => {
    if (spotLegDetails.upsRouteId) {
      const spotLegsId = spotLegDetails.spotLegsId;
      const params = {
        accepted: true,
        rejectionReason: null,
        rejectionNote: null,
      };

      if (isMultiLegRoute) {
        setCannotAssignCarrierFailureMessage(
          "Unable to Assign Leg in EPTS, this leg is part of a Multi-Leg Route. Please make the necessary changes directly in EPTS."
        );
        setCannotAssignCarrierModalOpen(true);
        return;
      }
      if (carrierId) {
        setIsAssigningCarrier(true);
        dispatch(
          assignSpotLegWithoutClaim(spotLegsId, carrierId, handleNotification)
        );
      } else {
        dispatch(
          processSpotLegClaim(
            spotLegClaim?.spotLegClaimsId,
            params,
            spotLegsId,
            handleNotification
          )
        );
      }
    } else {
      setUnableToPerformActionModalOpen(true);
    }
  };

  const handleUnableToPerformActionModalClose = () => {
    setUnableToPerformActionModalOpen(false);
  };

  const handleUnableToAssignCarrierModalClose = () => {
    setCannotAssignCarrierModalOpen(false);
  };

  useEffect(() => {
    dispatch(fetchSpotNotes(spotLegDetails.spotLegsId));

    if (spotLegClaim) {
      dispatch(fetchCarrier(spotLegClaim.carriersId));
      dispatch(fetchUser(spotLegClaim.claimingCarrierUserId));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unreadNotes = notes
      ?.filter((note) => !note.isRead && note.position === "left")
      .map((note) => note.notesId);

    if (
      spotLegDetails &&
      unreadNotes?.length > 0 &&
      putSpotNotesRequestStatus !== types.requestStatuses.inProgress
    ) {
      dispatch(markSpotNotesRead(spotLegDetails.spotLegsId, unreadNotes));
    }
  }, [dispatch, spotLegDetails, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      carrierId &&
      spotLegDetailsRequestStatus === types.requestStatuses.success
    ) {
      setIsAssigningCarrier(false);
      setCarrierAssigned(true);
      dispatch(fetchSpotNotes(spotLegDetails.spotLegsId));
    }
  }, [spotLegDetailsRequestStatus]);

  useEffect(() => {
    if (
      carrierId &&
      assignSpotLegWithoutClaimRequestStatus !==
      types.requestStatuses.inProgress
    ) {
      setIsAssigningCarrier(false);

      if (
        assignSpotLegWithoutClaimRequestStatus === types.requestStatuses.failure
      ) {
        dispatch(
          fetchSpotLegById(spotLegDetails.spotLegsId, handleNotification)
        );
        setCannotAssignCarrierFailureMessage(
          assignSpotLegWithoutClaimRequestFailureMessage
        );
        setCannotAssignCarrierModalOpen(true);
      }
    }
  }, [assignSpotLegWithoutClaimRequestStatus]);

  useEffect(() => {
    if (processClaimRequestStatus !== types.requestStatuses.inProgress) {
      setIsAssigningCarrier(false);

      if (processClaimRequestStatus === types.requestStatuses.failure) {
        setCannotAssignCarrierFailureMessage(processClaimRequestFailureMessage);
        setCannotAssignCarrierModalOpen(true);
      }
    }
  }, [processClaimRequestStatus]);

  useEffect(() => {
    if (
      spotLegDetails.status !== types.spotLegStatusEnum.coveredAssignedCarrier
    ) {
      setCarrierAssigned(false);
      setCarrierId(null);
    }
  }, [spotLegDetails.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRejectClaimModalClose = () => {
    setRejectClaimModalOpen(false);
  };

  const isSpotLegLoading =
    spotLegUpdateRequestStatus === types.requestStatuses.inProgress;
  const isSpotNotesLoading =
    types.requestStatuses.inProgress === getSpotNotesRequestStatus;
  const canAssignCarrier =
    spotLegDetails.status === types.spotLegStatusEnum.uncovered ||
    spotLegDetails.status === types.spotLegStatusEnum.posted;

  return (
    <>
      {unableToPerformActionModalOpen && (
        <ActionsModal
          open={unableToPerformActionModalOpen}
          title="Unable to Perform Action"
          message="Unable to assign carrier because this leg does not exist in EPTS. Once built in EPTS, please cancel in RouteConnect™ Available Routes."
          handleClose={handleUnableToPerformActionModalClose}
          handleAffirmative={handleUnableToPerformActionModalClose}
          affirmativeButtonText="CANCEL"
        />
      )}
      {rejectClaimModalOpen && (
        <RejectClaimModal
          open={rejectClaimModalOpen}
          handleClose={handleRejectClaimModalClose}
          claimId={spotLegClaim.spotLegClaimsId}
          spotLegId={spotLegDetails.spotLegsId}
          handleNotification={handleNotification}
        />
      )}
      {cannotAssignCarrierModalOpen && (
        <ActionsModal
          open={cannotAssignCarrierModalOpen}
          title="Unable to Assign Leg"
          message={cannotAssignCarrierFailureMessage}
          handleClose={handleUnableToAssignCarrierModalClose}
          handleAffirmative={handleUnableToAssignCarrierModalClose}
          affirmativeButtonText="CANCEL"
        />
      )}
      <UnsavedChangesModal
        open={unsavedModalOpen}
        handleAffirmative={handleModalAffirmative}
        handleNegative={handleModalNegative}
        message="This leg has unsaved changes. Leaving will discard those changes."
      />
      <div
        data-testid="ups-spot-leg-details"
        style={{ contain: "paint", minHeight: "100%" }}
      >
        <StyledHeader container alignItems="center" theme={theme}>
          <Grid item xs={2}>
            <span style={{ fontWeight: "bold" }}>ID:</span>{" "}
            {spotLegDetails.spotLegsId}
          </Grid>
          <Grid item xs={4}>
            <span style={{ fontWeight: "bold" }}>Route Code:</span>{" "}
            {spotLegDetails.routeCode}
          </Grid>
          <Grid item xs={3}>
            <span style={{ fontWeight: "bold" }}>Route ID:</span>{" "}
            {spotLegDetails.upsRouteId}
          </Grid>
          <Grid item xs={2}>
            {spotLegDetails.creationType === 1 ? (
              <>
                <StyledPersonIcon theme={theme} fontSize="small" /> Manual Ad
                Hoc
              </>
            ) : (
              <>
                <StyledDescriptionIcon theme={theme} fontSize="small" /> EPTS
                Upload
              </>
            )}
          </Grid>
          <Grid item xs={1}>
            <IconButton
              size="small"
              onClick={handleCloseButtonClick}
              style={{ float: "right" }}
            >
              <CloseIcon
                fontSize="small"
                data-testid="spot-leg-details-close-button"
              />
            </IconButton>
          </Grid>
        </StyledHeader>
        <div style={{ contain: "paint", minHeight: "100%" }}>
          <Backdrop
            open={
              isSpotNotesLoading ||
              isFetchingCarrier ||
              isFetchingUser ||
              isFetchingSpotLegDetails
            }
            style={{
              zIndex: theme.zIndex.drawer + 1,
              backgroundColor: "#fff",
              opacity: 1,
            }}
          >
            <Loader />
          </Backdrop>
          <StyledGridContainer container wrap="wrap">
            <Grid item component="h4" xs={12}>
              Leg Info
            </Grid>
            <SpotLegUpdate
              setUnsavedChanges={setUnsavedChanges}
              handleNotification={handleNotification}
              isAssigningCarrier={isAssigningCarrier}
            />
            <UpsSpotLegInfo />
          </StyledGridContainer>
          {(spotLegClaim || canAssignCarrier || carrierId) && (
            <>
              <StyledGridContainer
                container
                wrap="wrap"
                data-testid="pending-claim-section"
              >
                <Grid
                  item
                  component="h4"
                  xs={spotLegClaim?.proposedPickupTimeUtc ? 2 : 12}
                >
                  Pending Claim
                </Grid>
                {spotLegClaim?.proposedPickupTimeUtc && (
                  <Grid item xs={10} data-testid="schedule-change-text">
                    <Typography
                      style={{
                        paddingLeft: 8,
                        fontSize: 12,
                        color: theme.palette.primary.red,
                        lineHeight: 2.7,
                      }}
                    >
                      <AlertsIcon
                        style={{
                          fontSize: 12,
                          marginRight: 4,
                          verticalAlign: "middle",
                        }}
                        color={theme.palette.primary.red}
                      />
                      Requested schedule changes in red.
                    </Typography>
                  </Grid>
                )}
                <UpsSpotLegClaimInfo />
              </StyledGridContainer>
              {spotLegClaim && (
                <StyledGridContainer container wrap="wrap">
                  <Grid item xs={6}>
                    <Button
                      size="large"
                      style={{ height: "32px" }}
                      data-testid="reject-claim-btn"
                      onClick={handleRejectClaimButtonClick}
                    >
                      REJECT CLAIM
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <BaseButton
                      style={{ float: "right", width: "160px", height: "32px" }}
                      data-testid="assign-carrier-btn"
                      onClick={handleAssignCarrierButtonClick}
                    >
                      ASSIGN CARRIER
                    </BaseButton>
                  </Grid>
                </StyledGridContainer>
              )}
            </>
          )}
          {(canAssignCarrier || carrierAssigned) && !isMultiLegRoute && (
            <StyledGridContainer
              container
              spacing={2}
              style={{ paddingTop: 0 }}
              data-testid="assign-carrier-without-claim-container"
            >
              <Grid item xs={7}>
                <CarrierAutoComplete
                  setCarrierId={setCarrierId}
                  carrierId={carrierId}
                  isDisabled={carrierAssigned}
                />
              </Grid>
              <Grid item xs={5}>
                <BaseButton
                  style={{ height: "38px", width: "100%", marginTop: "8px" }}
                  data-testid="assign-carrier-without-claim-btn"
                  onClick={handleAssignCarrierButtonClick}
                  disabled={!carrierId || isSpotLegLoading || carrierAssigned}
                  startIcon={
                    isAssigningCarrier ? (
                      <CircularProgress size={15} />
                    ) : undefined
                  }
                >
                  {carrierAssigned ? (
                    <>
                      LEG ASSIGNED{" "}
                      <CheckIcon fontSize="small" style={{ marginLeft: 8 }} />
                    </>
                  ) : (
                    "ASSIGN CARRIER"
                  )}
                </BaseButton>
              </Grid>
            </StyledGridContainer>
          )}
          <Divider variant="middle" />
          <StyledGridContainer item container wrap="wrap">
            <Grid item component="h4">
              Note History
            </Grid>
            <NotesPanel notes={notes} isLoading={isSpotNotesLoading} />
          </StyledGridContainer>
        </div>
      </div>
    </>
  );
}

UpsSpotLegDetails.propTypes = {
  handleNotification: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
  unsavedChanges: PropTypes.bool.isRequired,
  setUnsavedModalOpen: PropTypes.func.isRequired,
  unsavedModalOpen: PropTypes.bool.isRequired,
};

export default UpsSpotLegDetails;
